import {
  AppointmentType,
  LocationGroup,
  LocationSummary,
  ProviderDetails,
  ProviderSummary,
  SchedulingDetails,
  SlotDateGroup,
  findAppointmentTypeInList,
} from '../../../../api/models';
import { DateUtility } from '@mchs/web-core';
import { getProviderAppointmentTypes } from '../../../../utils';
import { SchedulingUpdateContextType } from '../../../context/schedulingContext';

export const swapProviders = async (
  searchDate: Date,
  oldSchedulingDetails: SchedulingDetails,
  schedulingUpdateContext: SchedulingUpdateContextType,
  newProviderDetails: ProviderDetails,
  availableAppointmentSlots: SlotDateGroup[],
  oldSlotResults: SlotDateGroup[],
  oldAlternateProviders: ProviderSummary[],
  setSlotResults: React.Dispatch<React.SetStateAction<SlotDateGroup[]>>,
  setAlternateProviders: React.Dispatch<React.SetStateAction<ProviderSummary[]>>
) => {
  const oldProviderDetails = oldSchedulingDetails.providerDetails;
  const newSchedulingDetails: SchedulingDetails =
    await buildNewSchedulingDetails(oldSchedulingDetails, newProviderDetails);

  schedulingUpdateContext.changeSchedulingDetails(newSchedulingDetails);

  // remove selected provider from similar providers
  const alternatProviders = oldAlternateProviders.filter(
    (prv) => prv.provider?.id !== newProviderDetails.practitionerId
  );

  // add old provider to similar providers
  if (oldSlotResults !== undefined && oldSlotResults.length > 0) {
    alternatProviders.push(
      getOldProviderSummary(searchDate, oldProviderDetails, oldSlotResults)
    );
  }

  setAlternateProviders(alternatProviders);
  setSlotResults(availableAppointmentSlots ?? []);
};

const buildNewSchedulingDetails = async (
  oldSchedulingDetails: SchedulingDetails,
  newProviderDetails: ProviderDetails
): Promise<SchedulingDetails> => {
  let appointmentTypeList = await getProviderAppointmentTypes(
    newProviderDetails
  );

  const appointmentType = findAppointmentTypeInList(
    oldSchedulingDetails?.providerDetails?.appointmentTypeList,
    oldSchedulingDetails.appointmentInfo?.appointmentTypeId
  );

  appointmentTypeList = appointmentTypeList?.map(
    (apptType) =>
      apptType.id === appointmentType?.id
        ? (appointmentType as AppointmentType) // swap in the new appointment type with the questions added
        : apptType // don't worry about the questions as this isn't the current selection
  );

  const newSchedulingDetails: SchedulingDetails = {
    // keep anything already filled out the same
    // similar providers should have the same appointment types
    // with the same questions and at least on matching location
    ...oldSchedulingDetails,
    providerDetails: {
      ...newProviderDetails,
      appointmentTypeList: appointmentTypeList,
    },
  };

  return newSchedulingDetails;
};

const getOldProviderSummary = (
  searchDate: Date,
  oldProviderDetails: ProviderDetails | undefined,
  oldSlotResults: SlotDateGroup[]
): ProviderSummary => {
  const oldProviderLocationSummary: LocationSummary[] =
    getOldLocationSummaryList(oldSlotResults);

  return {
    daysUntilNextAppt: calculateDaysUntilNextAppt(searchDate, oldSlotResults),
    provider: {
      id: oldProviderDetails?.practitionerId,
      name: oldProviderDetails?.name,
      providerNumber: oldProviderDetails?.cattailsProviderId,
    },
    providerLocations: oldProviderLocationSummary,
    availableAppointmentSlots: oldSlotResults,
  };
};

const getOldLocationSummaryList = (
  oldSlotResults: SlotDateGroup[]
): LocationSummary[] => {
  const locationGroupList: LocationGroup[] = Array.prototype.concat.apply(
    [],
    oldSlotResults.map((slotResult) => slotResult.locationGroupList)
  );
  // add each location to the summary list once
  const oldProviderLocationSummary: LocationSummary[] = [];
  locationGroupList.forEach((locationGroup) => {
    if (
      oldProviderLocationSummary.findIndex(
        (locationSummary) =>
          locationSummary.locationId === locationGroup.locationId
      ) === -1
    ) {
      oldProviderLocationSummary.push({
        locationId: locationGroup.locationId,
        locationName: locationGroup.locationName,
        locationAddress: locationGroup.locationAddress,
      });
    }
  });

  return oldProviderLocationSummary;
};

const calculateDaysUntilNextAppt = (
  searchDate: Date,
  oldSlotResults: SlotDateGroup[]
): string => {
  const oldDate = oldSlotResults[0].appointmentDate;

  const oldDateWithoutTime = oldDate
    ? DateUtility.getDateWithoutTime(new Date(oldDate))
    : undefined;

  if (oldDateWithoutTime) {
    const curDateWithoutTime = DateUtility.getDateWithoutTime(searchDate);
    const diffTime =
      oldDateWithoutTime.getTime() - curDateWithoutTime.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 3600 * 24));
    return diffDays.toString();
  }

  return '?';
};
